/**
 * Typed fetch wrapper for the dashboard's cross-origin calls to the
 * Sill API (ADR-0018 §3, extended by ADR-0019 §3.7, §3.8, §4).
 *
 * One module owns the `credentials: 'include'` default, the
 * `content-type` header, the JSON body serialization, and the
 * response-to-typed-error mapping; every bag method routes through
 * this client.
 *
 * Why a hand-roll, not a typed-RPC library (per ADR-0018 §3 option A):
 * the surface ADR-0018 + ADR-0019 ships is small, the SillApi
 * contract is already typed via `@sill/types/api`, and adding a dep
 * (tRPC / ts-rest / hono RPC client) would be supply-chain surface
 * for a ~150-line wrapper.
 *
 * The 401 path is captured via `on_unauthorized` rather than thrown as
 * an exception because every authed call shares the same "session
 * expired → route to login" behavior; the callback lives at the
 * provider level (see `app/providers.tsx`) and the bootstrap auth
 * check + in-flight calls all funnel through it.
 *
 * ADR-0019 §3.7 — `requestBlob()` is the audit-bundle download path.
 * Same status-code mapping as `request()`, but reads the response
 * body as a Blob on 200 instead of JSON-parsing.
 *
 * ADR-0019 §3.8 — `request()`'s success branch widens to
 * `status === 200 || status === 201`. `POST /v1/sites` returns 201;
 * the pre-ADR-0019 client would surface successful site creation as
 * a generic error.
 *
 * ADR-0019 §4.2 — `ApiError` is the non-auth taxonomy. The auth-bag
 * classes (`AuthError`, `AuthUnauthorizedError`, `AuthRateLimitError`)
 * from `@sill/types/api` stay for backward compat with the auth bag;
 * the non-auth bags throw `ApiError` with a `kind` discriminant.
 */

import {
  AuthRateLimitError,
  AuthUnauthorizedError,
} from '@sill/types/api';

export type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

export interface HttpClientDeps {
  /**
   * Origin of the Sill API (services/api). Reads from `VITE_API_ORIGIN`
   * in production. Tests inject a fixture URL.
   */
  readonly api_origin: string;
  /**
   * Optional fetch override — production binds to `globalThis.fetch`,
   * tests inject a stub. Same pattern as the marketing site's
   * `submitSignIn` helper.
   */
  readonly fetch_impl?: FetchLike;
  /**
   * Called when any request returns 401. The dashboard's
   * `<AuthProvider>` wires this to "clear the cached currentUser and
   * route to the login view." ADR-0018 §2.3 — uniform 401 handling
   * across the auth bag. ADR-0019 §4.4 — uniform across non-auth
   * bags too; the callback fires on 401 regardless of which bag
   * issued the request.
   */
  readonly on_unauthorized?: () => void;
}

export interface HttpRequestOptions {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly body?: unknown;
  /**
   * Raw request body for non-JSON uploads (ADR-0103 catalog CSV import).
   * When set, the client sends this STRING verbatim as the body with the
   * caller-supplied `text_content_type` header instead of
   * JSON-serializing `body`. `body` is ignored when `text_body` is set.
   * Only `requestText()` honors this; `request()`/`requestBlob()` keep
   * the JSON-body contract. The response is still JSON-parsed.
   */
  readonly text_body?: string;
  /**
   * Content-Type for a `text_body` upload — e.g. `'text/csv'`. Required
   * when `text_body` is set; ignored otherwise.
   */
  readonly text_content_type?: string;
  /**
   * If true, the client maps 401 to {@link AuthUnauthorizedError}
   * WITHOUT invoking `on_unauthorized`. The bootstrap auth check
   * sets this so it can branch on the 401 inline (route to login)
   * rather than triggering the provider-level callback before the
   * provider is even rendered. Default false — in-flight calls from
   * mounted views go through the callback AND throw.
   */
  readonly throw_on_unauthorized?: boolean;
  /**
   * Hard timeout for the whole request, including reading the body.
   * Defaults to 20 s; `requestBlob` uses 120 s because a day's audit
   * bundle can run to thousands of records and the download alone can
   * outlast 20 s on an ordinary connection.
   */
  readonly timeout_ms?: number;
  /**
   * Per-request extra headers (ADR-0114 Slice B3 — needed for
   * `If-Match` on the agent-card-profile apply route). Each entry is
   * a `(name, value)` tuple; the client merges these into the request
   * `init.headers` AFTER setting the content-type so a caller cannot
   * accidentally override the JSON content-type. Header names are
   * lowercased on the wire by Fetch; pass them however you want.
   */
  readonly extra_headers?: Readonly<Record<string, string>>;
}

/**
 * ADR-0019 §4.2 — eleven failure modes the dashboard reasons about,
 * keyed off the HTTP status code or client-side condition.
 *
 *   - `unauthorized`        — 401: session expired or missing
 *   - `forbidden`           — 403: CORS-shape forbidden (defensive)
 *   - `not_found`           — 404: resource missing or RLS-filtered
 *   - `conflict`            — 409: e.g. duplicate site domain
 *   - `bad_request`         — 400: validation failure
 *   - `rate_limited`        — 429: sign-in only in Phase 1
 *   - `plan_limit_reached`  — 402: an entitlements cap-check helper
 *                             (ADR-0143 §8) blocked the request —
 *                             `err.plan_limit` carries the dimension /
 *                             limit / message the dashboard uses to
 *                             render an upgrade CTA instead of a raw
 *                             error.
 *   - `service_unavailable` — 503: KV / KMS downstream failure
 *   - `server_error`        — 500 + other unexpected 4xx/5xx
 *   - `network_error`       — fetch reject (network failure, CORS
 *                             denial dropped by browser), OR the
 *                             AbortSignal timeout fired on a GET.
 *                             Means "the request never left" / "no
 *                             answer at all" from the view's angle.
 *   - `network_timeout`     — the 20 s AbortSignal fired on a WRITE
 *                             method (POST/PATCH/PUT/DELETE). Means
 *                             "we gave up waiting; the server MAY
 *                             still have completed the write."
 *                             Distinct from `network_error` because
 *                             the UX is different: refresh to check
 *                             before retrying, don't just retry (the
 *                             founder hit this on a product save
 *                             that actually saved and on a held-
 *                             order approve, 2026-10-23). GET keeps
 *                             surfacing `network_error` on timeout
 *                             because a read retry is harmless.
 *   - `not_implemented`     — client-side: a method has no server
 *                             route (e.g. `sites.update`)
 *   - `bad_response`        — server returned 200 + JSON but the
 *                             envelope shape didn't match
 */
export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'bad_request'
  | 'rate_limited'
  | 'plan_limit_reached'
  | 'payload_too_large'
  | 'unsupported_media_type'
  | 'service_unavailable'
  | 'server_error'
  | 'network_error'
  | 'network_timeout'
  | 'not_implemented'
  | 'bad_response';

/**
 * Parsed body of a 402 entitlements cap-check block (ADR-0143 §8 —
 * `assertSitesWithinCap` / `assertRedTeamWithinCap` and future
 * dimensions share this shape: `{ dimension, limit, message }`, with
 * `count` present where the server tracks a live usage count). Set on
 * `ApiError.plan_limit` only when `kind === 'plan_limit_reached'`.
 */
export interface PlanLimitInfo {
  readonly dimension: string;
  readonly limit: number;
  readonly count?: number;
  readonly message: string;
}

/**
 * Single error class for the non-auth taxonomy. Callers branch on
 * `err.kind` to recover (e.g. `sites.get()` returns null on 'not_found',
 * `sites.create()` surfaces a typed message on 'conflict'). The
 * `status` field carries the HTTP status code for the cases where it
 * applies (null for `network_error`, `network_timeout`, `not_implemented`,
 * `bad_response`).
 *
 * `retry_after_seconds` is set only on `rate_limited` (parsed from the
 * `Retry-After` header). The auth bag's `AuthRateLimitError` carries
 * the converted minutes value; this class carries the raw seconds so
 * Phase 2 non-auth retries can use it without re-parsing.
 */
/** ADR-0216 — the API refuses writes from a read-only Sill support session
 *  with `403 {error:'support_session_read_only'}`. Every screen that shows
 *  `err.message` then says why, instead of "forbidden:403". */
export const SUPPORT_READ_ONLY_REASON = 'support_session_read_only';
export const SUPPORT_READ_ONLY_MESSAGE =
  'This is a read-only Sill support session, so changes are turned off.';
/** "Allow changes" support session hitting a locked area (payments,
 *  billing, team, deletion, key rotation). */
export const SUPPORT_RESTRICTED_REASON = 'support_session_restricted';
export const SUPPORT_RESTRICTED_MESSAGE =
  'Sill support cannot change payments, billing, team, keys or deletion. Ask the account owner.';

export class ApiError extends Error {
  constructor(
    public readonly kind: ApiErrorKind,
    public readonly status: number | null,
    public readonly retry_after_seconds?: number,
    /**
     * Optional server-supplied reason string, parsed from the response
     * body's `reason` field on 4xx/5xx responses. Populated by the
     * HttpClient before the error is thrown so dashboard error banners
     * can show "rule_0_allowlist_entry_malformed" instead of the
     * opaque "bad_request:400".
     */
    public readonly server_reason?: string,
    /**
     * Populated only when `kind === 'plan_limit_reached'` (402). Carries
     * the entitlements dimension/limit/message the server composed so
     * the dashboard can render an upgrade CTA instead of the raw
     * `server_reason` string.
     */
    public readonly plan_limit?: PlanLimitInfo,
    /**
     * Opaque server body for callers that need per-error-code context
     * beyond `server_reason` (ADR-0248 S3 `invalid_pattern` carries
     * `index + raw + reason` triplet). Null when the body wasn't
     * JSON-parseable.
     */
    public readonly server_body?: Record<string, unknown> | null,
  ) {
    super(
      server_reason === SUPPORT_READ_ONLY_REASON
        ? SUPPORT_READ_ONLY_MESSAGE
        : server_reason === SUPPORT_RESTRICTED_REASON
        ? SUPPORT_RESTRICTED_MESSAGE
        : plan_limit !== undefined
        ? `${kind}:${status ?? '?'} — ${plan_limit.message}`
        : server_reason !== undefined && server_reason.length > 0
          ? `${kind}:${status ?? '?'} — ${server_reason}`
          : `${kind}${status !== null ? `:${status}` : ''}`,
    );
    this.name = 'ApiError';
  }
}

export interface HttpClient {
  /**
   * Send a request and parse the JSON response body. Returns the
   * parsed body on 200 or 201 (typed as `T` — the caller asserts the
   * shape). Resolves with `undefined` on 204.
   */
  request<T = unknown>(opts: HttpRequestOptions): Promise<T | undefined>;
  /**
   * Same status-code mapping as `request()`, but returns the raw
   * response body as a Blob on 200. Used for the audit-bundle download
   * (`GET /v1/audit/:record_id/bundle`) where the body is the artifact
   * itself rather than a JSON envelope around it. ADR-0019 §3.7.
   */
  requestBlob(opts: HttpRequestOptions): Promise<Blob>;
  /**
   * Same status-code mapping + JSON response parse as `request()`, but
   * sends the `text_body` string verbatim with the `text_content_type`
   * header instead of a JSON body. The catalog CSV import
   * (`POST /v1/sites/:site_id/catalog/import`) is the first consumer —
   * the body IS the CSV (`text/csv`), not a JSON envelope around it
   * (ADR-0103). Optional so the many test stubs that implement the
   * client as `{ request, requestBlob }` keep type-checking; callers
   * that need it null-check before use.
   */
  requestText?<T = unknown>(opts: HttpRequestOptions): Promise<T | undefined>;
}

/**
 * Map a status code to the ApiError kind for the failure-status
 * branches. 401 / 429 are handled separately (legacy auth classes);
 * 200 / 201 / 202 / 204 are handled separately (success branches).
 */
function statusToKind(status: number): ApiErrorKind {
  if (status === 400) return 'bad_request';
  if (status === 402) return 'plan_limit_reached';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  // ADR-0103 catalog CSV import: 413 oversize / 415 wrong media type.
  if (status === 413) return 'payload_too_large';
  if (status === 415) return 'unsupported_media_type';
  if (status === 503) return 'service_unavailable';
  return 'server_error';
}

function parseRetryAfterSeconds(header: string | null): number {
  if (header === null) return 300;
  const n = Number(header);
  if (!Number.isFinite(n) || n <= 0) return 300;
  return Math.ceil(n);
}

/** Timeout for file downloads (audit bundles) — see `timeout_ms`. */
export const BLOB_TIMEOUT_MS = 120_000;

export function createHttpClient(deps: HttpClientDeps): HttpClient {
  const fetch_fn = deps.fetch_impl ?? globalThis.fetch.bind(globalThis);

  async function doFetch(opts: HttpRequestOptions): Promise<Response> {
    const url = `${deps.api_origin}${opts.path}`;
    // A raw text upload (CSV import, ADR-0103) takes precedence over a
    // JSON body — the body IS the artifact, sent verbatim with the
    // caller's content-type.
    const isTextBody = opts.text_body !== undefined;
    // ADR-0114 Slice B3 — merge `extra_headers` AFTER the content-type
    // so a caller cannot accidentally override it. The If-Match header
    // for the agent-card-profile apply route rides this path.
    const base_headers: Record<string, string> = isTextBody
      ? { 'content-type': opts.text_content_type ?? 'text/plain' }
      : opts.body !== undefined
        ? { 'content-type': 'application/json' }
        : {};
    const merged_headers: Record<string, string> =
      opts.extra_headers !== undefined
        ? { ...opts.extra_headers, ...base_headers }
        : base_headers;
    const init: RequestInit = {
      method: opts.method,
      // ADR-0018 §3 / ADR-0019 §5.1 — dashboard fetches carry
      // credentials so the session cookie (sill_session,
      // Domain=.sill.so per ADR-0017) is auto-attached. This is the
      // inverse of the marketing submitSignIn helper which uses
      // 'omit' because that path is cookie-free.
      credentials: 'include',
      headers: merged_headers,
      ...(isTextBody
        ? { body: opts.text_body }
        : opts.body !== undefined
          ? { body: JSON.stringify(opts.body) }
          : {}),
    };

    try {
      // 20-second hard timeout so a hung connection fails fast rather
      // than causing a prolonged blank loader that eventually kicks
      // the user to the login page with no explanation.
      const signal = AbortSignal.timeout(opts.timeout_ms ?? 20_000);
      return await fetch_fn(url, { ...init, signal });
    } catch (cause) {
      // Distinguish a timeout from an immediate network failure. The
      // 20 s AbortSignal.timeout() rejects with a DOMException whose
      // `name` is `'TimeoutError'`; everything else (DNS fail, CORS
      // drop, offline) is a `TypeError` or an abort-signal-of-type-
      // other. On a WRITE method the distinction matters: the server
      // may have completed the write while the browser-side signal
      // already aborted (founder report, 2026-10-23). Views surface
      // "This may have saved — refresh to check" for `network_timeout`
      // instead of the misleading "Network error".
      //
      // GETs keep the old `network_error` kind on timeout — a read
      // retry is harmless, so the extra "may have saved" copy would
      // confuse rather than clarify.
      const is_timeout =
        typeof DOMException !== 'undefined'
        && cause instanceof DOMException
        && cause.name === 'TimeoutError';
      const is_write = opts.method !== 'GET';
      throw new ApiError(
        is_timeout && is_write ? 'network_timeout' : 'network_error',
        null,
      );
    }
  }

  /**
   * Common status-code handling shared by `request` and `requestBlob`.
   * Returns `undefined` on 204, `null` if the caller should proceed to
   * the success branch (200/201), or throws for everything else.
   *
   * Async because 4xx/5xx responses get their body parsed for a
   * `reason` field that's included in the thrown ApiError.
   */
  async function handleNonSuccessStatuses(
    res: Response,
    opts: HttpRequestOptions,
  ): Promise<undefined | null> {
    // 401 — uniform unauthorized handling. ADR-0018 §2.3 + ADR-0019
    // §4.4: the on_unauthorized callback fires for every bag, not
    // just the auth bag. The bootstrap auth check sets
    // `throw_on_unauthorized` to handle the 401 inline.
    if (res.status === 401) {
      if (opts.throw_on_unauthorized !== true) {
        deps.on_unauthorized?.();
      }
      throw new AuthUnauthorizedError();
    }

    // 429 — rate limit. Auth-bag (sign-in) surfaces the Retry-After-
    // derived minutes via `AuthRateLimitError`. Non-auth callers
    // (none in Phase 1) would catch ApiError('rate_limited') if a
    // future route returns 429. Here we throw AuthRateLimitError
    // for backward compat with the auth bag's expected shape.
    if (res.status === 429) {
      const retry_after = res.headers.get('retry-after');
      const minutes =
        retry_after !== null
          ? Math.max(1, Math.ceil(Number(retry_after) / 60))
          : 5;
      // ADR-0103 — read the body `reason` so a non-auth 429 (e.g. the
      // catalog manual-sync trigger: `daily_cap` vs `too_soon`) can be
      // distinguished by callers. The sign-in 429 carries no body, so the
      // parse fails silently and `server_reason` stays undefined (the auth
      // bag never reads it). Same defensive JSON parse as the 4xx/5xx branch.
      let server_reason: string | undefined;
      // ADR-0263 §7.5 as amended for security (2026-11-03) — pass
      // the full parsed body on `AuthRateLimitError.server_body` so
      // callers can lift `message`, `used`, `limit`, `remaining`
      // etc. from the new per-recipient and monthly-scan 429s.
      let server_body: Record<string, unknown> | null = null;
      try {
        const body = (await res.json()) as Record<string, unknown>;
        if (typeof body.reason === 'string' && body.reason.length > 0) {
          server_reason = body.reason;
        }
        server_body = body;
      } catch {
        /* non-JSON / empty body — proceed without a reason */
      }
      throw new AuthRateLimitError(minutes, server_reason, server_body);
    }

    // 204 — no body to parse.
    if (res.status === 204) {
      return undefined;
    }

    // 200 / 201 / 202 — success branch. Caller continues to JSON-parse.
    // ADR-0019 §3.8 — POST /v1/sites returns 201. ADR-0040 — the
    // profile-extraction trigger POST returns 202 Accepted (fire-and-
    // forget; the body is the `{ profile_extraction_id, status:
    // 'running' }` ack the dashboard polls on). WITHOUT 202 here, that
    // 202 fell through to the error branch below and threw
    // `server_error`, so `start()` threw, `beginPolling` never ran, and
    // the merchant got "Couldn't analyze your site" on every click while
    // the backend run actually succeeded. Every JSON-body 2xx parses
    // through this branch.
    if (res.status === 200 || res.status === 201 || res.status === 202) {
      return null;
    }

    // Any other status — typed as ApiError with the canonical kind.
    // Read the response body's `reason` field if present so the
    // dashboard surface can show the specific server-side cause
    // instead of an opaque `bad_request:400`. Non-JSON bodies are
    // tolerated; the absence of `reason` is the same as no reason.
    //
    // ADR-0240 Portal v2 Slice 1 (2026-10-10 — agency send-outcome
    // bug fix): several agency routes emit `{ error: '<code>' }`
    // WITHOUT a mirrored `reason`, so the specific-cause branches
    // in `agency-reports.ts` / `agency-widget.ts` / `account-
    // white-label.ts` are unreachable and every failure collapses
    // to the opaque `server_error:502` / `conflict:409` message.
    // Belt-and-braces: when `reason` is absent, fall back to
    // `body.error` IFF it matches the server's own enum shape
    // (lowercase + underscore, 3–64 chars). The server fix lands
    // in the same slice (routes mirror `reason`), so the fallback
    // is defensive, not primary.
    let server_reason: string | undefined;
    let plan_limit: PlanLimitInfo | undefined;
    let server_body: Record<string, unknown> | null = null;
    try {
      const body = (await res.json()) as {
        error?: unknown;
        reason?: unknown;
        dimension?: unknown;
        limit?: unknown;
        count?: unknown;
        message?: unknown;
      };
      if (body !== null && typeof body === 'object') {
        server_body = body as Record<string, unknown>;
      }
      if (typeof body.reason === 'string' && body.reason.length > 0) {
        server_reason = body.reason;
      }
      if (
        server_reason === undefined
        && typeof body.error === 'string'
        && /^[a-z_]{3,64}$/.test(body.error)
      ) {
        server_reason = body.error;
      }
      if (body.error === SUPPORT_READ_ONLY_REASON || body.error === SUPPORT_RESTRICTED_REASON) {
        server_reason = body.error;
      }
      // 402 entitlements cap-check shape (ADR-0143 §8) — `dimension` +
      // `limit` + `message` are always present together; `count` is
      // optional (not every dimension tracks a live usage count).
      if (
        res.status === 402
        && typeof body.dimension === 'string'
        && typeof body.limit === 'number'
        && typeof body.message === 'string'
      ) {
        plan_limit = {
          dimension: body.dimension,
          limit: body.limit,
          message: body.message,
          ...(typeof body.count === 'number' ? { count: body.count } : {}),
        };
      }
    } catch {
      /* non-JSON / empty body — proceed without a reason */
    }
    throw new ApiError(
      statusToKind(res.status),
      res.status,
      undefined,
      server_reason,
      plan_limit,
      server_body,
    );
  }

  return {
    async request<T = unknown>(opts: HttpRequestOptions): Promise<T | undefined> {
      const res = await doFetch(opts);
      const dispatch = await handleNonSuccessStatuses(res, opts);
      if (dispatch === undefined) return undefined;
      try {
        return (await res.json()) as T;
      } catch {
        throw new ApiError('bad_response', null);
      }
    },

    async requestText<T = unknown>(
      opts: HttpRequestOptions,
    ): Promise<T | undefined> {
      const res = await doFetch(opts);
      const dispatch = await handleNonSuccessStatuses(res, opts);
      if (dispatch === undefined) return undefined;
      try {
        return (await res.json()) as T;
      } catch {
        throw new ApiError('bad_response', null);
      }
    },

    async requestBlob(opts: HttpRequestOptions): Promise<Blob> {
      const res = await doFetch({ timeout_ms: BLOB_TIMEOUT_MS, ...opts });
      const dispatch = await handleNonSuccessStatuses(res, opts);
      // 204 on a Blob route is malformed — the audit-bundle endpoint
      // never returns 204, but `dispatch === undefined` means the
      // server replied with no body where one was expected.
      if (dispatch === undefined) {
        throw new ApiError('bad_response', null);
      }
      try {
        return await res.blob();
      } catch {
        throw new ApiError('bad_response', null);
      }
    },
  };
}
